import { useCallback, useState } from 'react'
import axios, { isAxiosError } from 'axios'
import { apiClient } from '@/shared/api/apiClient'
import {
  organizationDocumentMaxFileSizeBytes,
  organizationDocumentUploadTimeoutMs,
} from '@/shared/lib/organization-document-limits'

type DownloadResult = {
  success: boolean
  errorMessage?: string
}

type UploadResult = {
  success: boolean
  errorMessage?: string
  fileName?: string
}

const ERRORS = {
  FORBIDDEN: 'FORBIDDEN',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  FAILED_TO_GENERATE_UPLOAD_URL: 'FAILED_TO_GENERATE_UPLOAD_URL',
  FAILED_TO_GENERATE_DOWNLOAD_URL: 'FAILED_TO_GENERATE_DOWNLOAD_URL',
  FAILED_TO_UPLOAD_FILE: 'FAILED_TO_UPLOAD_FILE',
  FAILED_TO_DOWNLOAD_FILE: 'FAILED_TO_DOWNLOAD_FILE',
  INVALID_FILE_TYPE: 'INVALID_FILE_TYPE',
  UPLOAD_TIMED_OUT: 'UPLOAD_TIMED_OUT',
  UPLOAD_ABORTED: 'UPLOAD_ABORTED',
} as const

type UploadDocumentOptions = {
  signal?: AbortSignal
}

const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'application/pdf',
  'application/xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]

function validateFileType(fileType: string) {
  return ALLOWED_FILE_TYPES.includes(fileType)
}

type UseOrganizationDocumentsParams = {
  organizationId: string
}

export function useOrganizationDocuments({
  organizationId,
}: UseOrganizationDocumentsParams) {
  const [documentUploadState, setDocumentUploadState] = useState<
    'error' | 'uploading' | null | 'success'
  >(null)

  const resetDocumentUploadState = useCallback(() => {
    setDocumentUploadState(null)
  }, [])

  const uploadToBucket = useCallback(
    async (file: File, options?: UploadDocumentOptions) => {
      if (!validateFileType(file.type)) {
        throw new Error(ERRORS.INVALID_FILE_TYPE)
      }

      if (file.size > organizationDocumentMaxFileSizeBytes) {
        throw new Error(ERRORS.FILE_TOO_LARGE)
      }

      let signedUploadUrl: string | null = null
      let normalizedKey: string | null = null
      let errorMessage: string | null = null
      try {
        const response = await apiClient.post<{ url: string; normalizedKey: string }>(
          `/v1/organizations/${organizationId}/generate-upload-url`,
          {
            key: file.name,
          },
          {
            signal: options?.signal,
          }
        )

        signedUploadUrl = response.data.url
        normalizedKey = response.data.normalizedKey
      } catch (err) {
        if (isAxiosError(err) && err.code === 'ERR_CANCELED') {
          throw new Error(ERRORS.UPLOAD_ABORTED)
        }
        errorMessage = ERRORS.FAILED_TO_GENERATE_UPLOAD_URL
      }

      if (!signedUploadUrl || !normalizedKey) {
        return
      }

      try {
        await axios.put(signedUploadUrl, file, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          timeout: organizationDocumentUploadTimeoutMs,
          signal: options?.signal,
        })
      } catch (err) {
        if (isAxiosError(err)) {
          if (err.code === 'ERR_CANCELED') {
            throw new Error(ERRORS.UPLOAD_ABORTED)
          }
          if (err.code === 'ECONNABORTED') {
            errorMessage = ERRORS.UPLOAD_TIMED_OUT
          } else if (err.response?.status === 403) {
            errorMessage = ERRORS.FORBIDDEN
          } else if (err.response?.status === 413) {
            errorMessage = ERRORS.FILE_TOO_LARGE
          } else {
            errorMessage = ERRORS.FAILED_TO_UPLOAD_FILE
          }
        }
      }

      if (errorMessage) {
        throw new Error(errorMessage)
      }

      return normalizedKey
    },
    [organizationId]
  )

  const uploadDocument = useCallback(
    async (file?: File, options?: UploadDocumentOptions): Promise<UploadResult> => {
      if (!file) {
        return { success: false, errorMessage: ERRORS.FAILED_TO_UPLOAD_FILE }
      }

      setDocumentUploadState('uploading')
      try {
        const normalizedKey = await uploadToBucket(file, options)

        if (!normalizedKey) {
          setDocumentUploadState('error')
          return { success: false, errorMessage: ERRORS.FAILED_TO_UPLOAD_FILE }
        }

        setDocumentUploadState('success')

        return { success: true, fileName: normalizedKey }
      } catch (error: unknown) {
        let errorMessage: string = ERRORS.FAILED_TO_UPLOAD_FILE

        if (error instanceof Error && error.message) {
          errorMessage = error.message
        }

        if (errorMessage === ERRORS.UPLOAD_ABORTED) {
          setDocumentUploadState(null)
          return { success: false, errorMessage }
        }

        setDocumentUploadState('error')
        return { success: false, errorMessage }
      }
    },
    [uploadToBucket]
  )

  const generateDownloadUrl = useCallback(
    async (fileName: string) => {
      let downloadUrl: string | null = null
      let errorMessage: string | null = null
      try {
        const response = await apiClient.post<{ url: string }>(
          `/v1/organizations/${organizationId}/generate-download-url`,
          {
            key: fileName,
          }
        )

        downloadUrl = response.data.url
      } catch {
        errorMessage = ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL
      }

      if (!downloadUrl) {
        return
      }
      if (errorMessage) {
        throw new Error(errorMessage)
      }

      return downloadUrl
    },
    [organizationId]
  )

  const downloadDocument = useCallback(
    async (fileName: string): Promise<DownloadResult> => {
      try {
        const downloadUrl = await generateDownloadUrl(fileName)
        if (!downloadUrl) {
          return {
            success: false,
            errorMessage: ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL,
          }
        }

        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        return { success: true }
      } catch (error: unknown) {
        if (error instanceof Error) {
          if (error.message === ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL) {
            return {
              success: false,
              errorMessage: ERRORS.FAILED_TO_GENERATE_DOWNLOAD_URL,
            }
          }
        }
        return { success: false, errorMessage: ERRORS.FAILED_TO_DOWNLOAD_FILE }
      }
    },
    [generateDownloadUrl]
  )

  return {
    resetDocumentUploadState,
    uploadToBucket,
    uploadDocument,
    generateDownloadUrl,
    downloadDocument,
    documentUploadState,
  }
}
